/**
 * Registry-driven funding appeal landing + finished-appeal page templates.
 * Editors map field types to controls; renderers produce deterministic HTML.
 */

import sanitizeHtml from "sanitize-html";
import { formatCurrencyCents } from "./format";
import { resolveFundingLandingPageMarkers } from "./fundingAppealLandingMarkers";
import {
  type LandingBlock,
  type LandingPageRenderContext,
  type LandingPageStyle,
  renderLandingPageHtmlFromBlocks,
} from "./fundingLandingPageBlocks";
import type { FundingDonationTier } from "./fundingDonationTiers";
import type { FundingClubSocialLinksContext } from "./fundingClubSocialLinks";

export const FUNDING_PAGE_FIELD_TYPES = ["plainText", "richText", "image", "enum", "boolean"] as const;
export type FundingPageFieldType = (typeof FUNDING_PAGE_FIELD_TYPES)[number];

export type FundingPageFieldOption = { value: string; label: string };

export type FundingPageFieldDef = {
  id: string;
  label: string;
  type: FundingPageFieldType;
  required?: boolean;
  placeholder?: string;
  defaultValue?: string | boolean;
  options?: FundingPageFieldOption[];
};

export type FundingPageSectionDef = {
  id: string;
  label: string;
  /** Boolean field id controlling whether this section is shown on the public page. */
  visibilityFieldId: string;
  fields: FundingPageFieldDef[];
};

export type FundingPageThemePreset = {
  id: string;
  label: string;
  pageStyle: LandingPageStyle;
};

export type FundingPageTemplateDef = {
  id: string;
  label: string;
  themePresets: FundingPageThemePreset[];
  defaultThemePresetId: string;
  landingSections: FundingPageSectionDef[];
};

/** Persisted template configuration (stored in `template_config` JSONB). */
export type FundingPageTemplateConfig = {
  templateId: string;
  themePresetId: string;
  fields: Record<string, string | boolean>;
};

export type FundingPageRenderOptions = {
  appealId: string;
  inboundEmailDomain?: string;
  landingEnabled?: boolean;
  /** Finished-appeal page (post-close destination URL). Stored in `success_*` columns. */
  finishedEnabled?: boolean;
  /** Embed a static contact form in landing HTML (admin preview only). */
  contactFormPreview?: boolean;
  clubSocialLinks?: FundingClubSocialLinksContext;
  donationTiers?: FundingDonationTier[];
  currency?: string;
};

const RICH_TEXT_ALLOWED_TAGS = [
  "p",
  "br",
  "a",
  "strong",
  "em",
  "b",
  "i",
  "u",
  "ul",
  "ol",
  "li",
  "blockquote",
] as const;

const richTextSanitizeOptions: sanitizeHtml.IOptions = {
  allowedTags: [...RICH_TEXT_ALLOWED_TAGS],
  allowedAttributes: {
    a: ["href", "target", "rel"],
  },
  allowedSchemes: ["http", "https", "mailto"],
  transformTags: {
    a: (tagName, attribs) => ({
      tagName,
      attribs: { ...attribs, target: "_blank", rel: "noopener noreferrer" },
    }),
  },
};

/** Sanitize rich-text field input (editor + defense in depth on render). */
export function sanitizeFundingPageRichText(html: string): string {
  return sanitizeHtml(html ?? "", richTextSanitizeOptions);
}

export const FUNDING_PAGE_THEME_PRESETS: FundingPageThemePreset[] = [
  {
    id: "classic",
    label: "Classic blue",
    pageStyle: { accentColor: "#1d4ed8", textColor: "#ffffff" },
  },
  {
    id: "forest",
    label: "Forest green",
    pageStyle: { accentColor: "#166534", textColor: "#ffffff" },
  },
  {
    id: "sunset",
    label: "Sunset",
    pageStyle: { accentColor: "#c2410c", textColor: "#ffffff" },
  },
];

const DEFAULT_APPEAL_TEMPLATE: FundingPageTemplateDef = {
  id: "appeal-default",
  label: "Appeal (default)",
  themePresets: FUNDING_PAGE_THEME_PRESETS,
  defaultThemePresetId: "classic",
  landingSections: [
    {
      id: "hero",
      label: "Header",
      visibilityFieldId: "section_hero_visible",
      fields: [
        {
          id: "hero_title",
          label: "Title",
          type: "plainText",
          required: true,
          defaultValue: "Support our appeal",
        },
        {
          id: "hero_subtitle",
          label: "Subtitle",
          type: "plainText",
          defaultValue: "Help us reach our fundraising goal",
        },
        { id: "hero_image_url", label: "Header image", type: "image", defaultValue: "" },
        {
          id: "hero_image_alt",
          label: "Header image description",
          type: "plainText",
          defaultValue: "",
        },
      ],
    },
    {
      id: "story",
      label: "Story",
      visibilityFieldId: "section_story_visible",
      fields: [
        {
          id: "story_heading",
          label: "Heading",
          type: "plainText",
          defaultValue: "Why we need your support",
        },
        {
          id: "story_body",
          label: "Body",
          type: "richText",
          defaultValue: "<p>Tell donors what you are raising money for and how their support will help.</p>",
        },
      ],
    },
    {
      id: "donation_tiers",
      label: "Donation tiers",
      visibilityFieldId: "section_donation_tiers_visible",
      fields: [
        {
          id: "donation_tiers_heading",
          label: "Heading",
          type: "plainText",
          defaultValue: "Suggested support levels",
        },
        {
          id: "donation_tiers_body",
          label: "Description",
          type: "plainText",
          defaultValue: "Choose a level that works for you — we appreciate every contribution.",
        },
        {
          id: "donation_tiers_button_label",
          label: "Button label",
          type: "plainText",
          defaultValue: "Get in touch",
        },
      ],
    },
    {
      id: "impact",
      label: "Key figures",
      visibilityFieldId: "section_impact_visible",
      fields: [
        { id: "impact_heading", label: "Section heading", type: "plainText", defaultValue: "Your impact" },
        {
          id: "impact_1_value",
          label: "Amount",
          type: "plainText",
          defaultValue: "",
          placeholder: "Uses fundraising goal when empty",
        },
        { id: "impact_1_label", label: "Caption", type: "plainText", defaultValue: "Fundraising goal" },
        { id: "impact_2_value", label: "Amount", type: "plainText", defaultValue: "120" },
        { id: "impact_2_label", label: "Caption", type: "plainText", defaultValue: "Members" },
      ],
    },
    {
      id: "image",
      label: "Image",
      visibilityFieldId: "section_image_visible",
      fields: [
        { id: "feature_image_url", label: "Image", type: "image", defaultValue: "" },
        { id: "feature_image_alt", label: "Image description", type: "plainText", defaultValue: "" },
      ],
    },
    {
      id: "social",
      label: "Social links",
      visibilityFieldId: "section_social_visible",
      fields: [
        {
          id: "social_show_directory",
          label: "MixItOne directory",
          type: "boolean",
          defaultValue: true,
        },
        { id: "social_show_facebook", label: "Facebook", type: "boolean", defaultValue: true },
        { id: "social_show_website", label: "Website", type: "boolean", defaultValue: true },
      ],
    },
    {
      id: "email",
      label: "Email link",
      visibilityFieldId: "section_email_visible",
      fields: [
        { id: "email_headline", label: "Headline", type: "plainText", defaultValue: "Email us" },
        {
          id: "email_body",
          label: "Description",
          type: "plainText",
          defaultValue: "Prefer email? Send us a message directly.",
        },
        { id: "email_link_label", label: "Button label", type: "plainText", defaultValue: "Send email" },
      ],
    },
    {
      id: "contact",
      label: "Contact form",
      visibilityFieldId: "section_contact_visible",
      fields: [
        { id: "contact_headline", label: "Headline", type: "plainText", defaultValue: "Get in touch" },
        {
          id: "contact_body",
          label: "Description",
          type: "plainText",
          defaultValue: "Send a message and we will get back to you.",
        },
        {
          id: "contact_submit_label",
          label: "Submit button",
          type: "plainText",
          defaultValue: "Send message",
        },
      ],
    },
  ],
};

export const FUNDING_PAGE_TEMPLATES: FundingPageTemplateDef[] = [DEFAULT_APPEAL_TEMPLATE];

export const DEFAULT_FUNDING_PAGE_TEMPLATE_ID = DEFAULT_APPEAL_TEMPLATE.id;

export function getFundingPageTemplate(templateId: string): FundingPageTemplateDef {
  const found = FUNDING_PAGE_TEMPLATES.find((t) => t.id === templateId);
  if (!found) throw new Error(`Unknown funding page template: ${templateId}`);
  return found;
}

export function getFundingPageThemePreset(
  template: FundingPageTemplateDef,
  presetId: string,
): FundingPageThemePreset {
  const found = template.themePresets.find((p) => p.id === presetId);
  if (found) return found;
  const fallback = template.themePresets.find((p) => p.id === template.defaultThemePresetId);
  if (fallback) return fallback;
  throw new Error(`Unknown theme preset: ${presetId}`);
}

/** Theme preset selector field (enum over registry entries). */
export function fundingPageThemePresetField(template: FundingPageTemplateDef): FundingPageFieldDef {
  return {
    id: "theme_preset",
    label: "Theme",
    type: "enum",
    defaultValue: template.defaultThemePresetId,
    options: template.themePresets.map((p) => ({ value: p.id, label: p.label })),
  };
}

function defaultFieldValues(template: FundingPageTemplateDef): Record<string, string | boolean> {
  const values: Record<string, string | boolean> = {
    theme_preset: template.defaultThemePresetId,
  };
  for (const section of template.landingSections) {
    values[section.visibilityFieldId] = true;
    for (const field of section.fields) {
      if (field.defaultValue !== undefined) values[field.id] = field.defaultValue;
      else if (field.type === "boolean") values[field.id] = false;
      else values[field.id] = "";
    }
  }
  return values;
}

/** Legacy template placeholder replaced when an appeal goal is available. */
export const LEGACY_IMPACT_1_VALUE_DEFAULT = "£5,000";

export type FundingPageAppealDefaults = {
  targetCents?: number | null;
  currency?: string | null;
  donationTiers?: FundingDonationTier[];
};

function impact1ValueNeedsAppealGoal(value: unknown): boolean {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed === "" || trimmed === LEGACY_IMPACT_1_VALUE_DEFAULT;
}

/** Prefill the first key figure from the appeal fundraising goal when the slot is still empty. */
export function applyAppealGoalToFundingPageTemplateConfig(
  config: FundingPageTemplateConfig,
  appeal: FundingPageAppealDefaults,
): FundingPageTemplateConfig {
  const target = appeal.targetCents;
  if (target == null || !Number.isFinite(target) || target <= 0) return config;
  if (!impact1ValueNeedsAppealGoal(config.fields.impact_1_value)) return config;

  const currency = (appeal.currency ?? "usd").trim() || "usd";
  return {
    ...config,
    fields: {
      ...config.fields,
      impact_1_value: formatCurrencyCents(target, currency),
    },
  };
}

export function createDefaultFundingPageTemplateConfig(
  templateId: string = DEFAULT_FUNDING_PAGE_TEMPLATE_ID,
  appealDefaults?: FundingPageAppealDefaults,
): FundingPageTemplateConfig {
  const template = getFundingPageTemplate(templateId);
  const base: FundingPageTemplateConfig = {
    templateId: template.id,
    themePresetId: template.defaultThemePresetId,
    fields: defaultFieldValues(template),
  };
  return appealDefaults ? applyAppealGoalToFundingPageTemplateConfig(base, appealDefaults) : base;
}

/** Defaults for the finished-appeal page (same sections; contact form hidden by default). */
export function createDefaultFundingPageFinishedConfig(
  templateId: string = DEFAULT_FUNDING_PAGE_TEMPLATE_ID,
  appealDefaults?: FundingPageAppealDefaults,
): FundingPageTemplateConfig {
  const base = createDefaultFundingPageTemplateConfig(templateId, appealDefaults);
  return {
    ...base,
    fields: {
      ...base.fields,
      section_contact_visible: false,
      hero_title: "Thank you for your support",
      hero_subtitle: "This appeal has now closed",
    },
  };
}

export function normalizeFundingPageFinishedConfig(
  raw: unknown,
  templateId: string = DEFAULT_FUNDING_PAGE_TEMPLATE_ID,
): FundingPageTemplateConfig {
  const isEmpty =
    raw == null ||
    (typeof raw === "object" &&
      Object.keys(raw as object).length === 0 &&
      !(raw as { fields?: unknown }).fields);
  if (isEmpty) return createDefaultFundingPageFinishedConfig(templateId);
  return normalizeFundingPageTemplateConfig(raw, templateId);
}

export function normalizeFundingPageTemplateConfig(
  raw: unknown,
  templateId: string = DEFAULT_FUNDING_PAGE_TEMPLATE_ID,
): FundingPageTemplateConfig {
  const template = getFundingPageTemplate(
    typeof raw === "object" && raw != null && typeof (raw as { templateId?: string }).templateId === "string"
      ? (raw as { templateId: string }).templateId
      : templateId,
  );
  const defaults = defaultFieldValues(template);
  const base = createDefaultFundingPageTemplateConfig(template.id);
  if (!raw || typeof raw !== "object") return base;

  const input = raw as Partial<FundingPageTemplateConfig> & { fields?: Record<string, unknown> };
  const fields: Record<string, string | boolean> = { ...defaults };
  if (input.fields && typeof input.fields === "object") {
    for (const [key, val] of Object.entries(input.fields)) {
      if (typeof val === "string" || typeof val === "boolean") fields[key] = val;
    }
  }
  const themeFromFields =
    typeof fields.theme_preset === "string" ? fields.theme_preset : template.defaultThemePresetId;
  const themePresetId =
    typeof input.themePresetId === "string" && template.themePresets.some((p) => p.id === input.themePresetId)
      ? input.themePresetId
      : template.themePresets.some((p) => p.id === themeFromFields)
        ? themeFromFields
        : template.defaultThemePresetId;
  fields.theme_preset = themePresetId;

  return {
    templateId: template.id,
    themePresetId,
    fields,
  };
}

function fieldBool(config: FundingPageTemplateConfig, fieldId: string, fallback = true): boolean {
  const v = config.fields[fieldId];
  if (typeof v === "boolean") return v;
  return fallback;
}

function fieldString(config: FundingPageTemplateConfig, fieldId: string): string {
  const v = config.fields[fieldId];
  return typeof v === "string" ? v : "";
}

function sectionVisible(config: FundingPageTemplateConfig, section: FundingPageSectionDef): boolean {
  return fieldBool(config, section.visibilityFieldId, true);
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Map template config to landing blocks for the shared appeal renderer. */
export function fundingPageConfigToLandingBlocks(
  config: FundingPageTemplateConfig,
  template: FundingPageTemplateDef,
): LandingBlock[] {
  const blocks: LandingBlock[] = [];
  const str = (id: string) => fieldString(config, id).trim();

  if (sectionVisible(config, template.landingSections.find((s) => s.id === "hero")!)) {
    const title = str("hero_title") || "Support our appeal";
    const subtitle = str("hero_subtitle");
    const imageSrc = str("hero_image_url");
    const imageAlt = str("hero_image_alt");
    blocks.push({
      type: "hero",
      title,
      ...(subtitle ? { subtitle } : {}),
      ...(imageSrc ? { imageSrc, ...(imageAlt ? { imageAlt } : {}) } : {}),
    });
  }

  // Story section uses richText — rendered in renderFundingLandingPageHtml, not via blocks.

  if (sectionVisible(config, template.landingSections.find((s) => s.id === "donation_tiers")!)) {
    blocks.push({
      type: "donation_tiers",
      ...(str("donation_tiers_heading") ? { heading: str("donation_tiers_heading") } : {}),
      ...(str("donation_tiers_body") ? { body: str("donation_tiers_body") } : {}),
      buttonLabel: str("donation_tiers_button_label") || "Get in touch",
    });
  }

  if (sectionVisible(config, template.landingSections.find((s) => s.id === "impact")!)) {
    const items: Array<{ value: string; label: string }> = [];
    const v1 = str("impact_1_value");
    const l1 = str("impact_1_label");
    const v2 = str("impact_2_value");
    const l2 = str("impact_2_label");
    if (v1 && l1) items.push({ value: v1, label: l1 });
    if (v2 && l2) items.push({ value: v2, label: l2 });
    if (items.length > 0) {
      blocks.push({
        type: "impact",
        ...(str("impact_heading") ? { heading: str("impact_heading") } : {}),
        items,
      });
    }
  }

  if (sectionVisible(config, template.landingSections.find((s) => s.id === "image")!)) {
    const src = str("feature_image_url");
    const alt = str("feature_image_alt") || "Appeal image";
    if (src) blocks.push({ type: "image", src, alt });
  }

  if (sectionVisible(config, template.landingSections.find((s) => s.id === "social")!)) {
    blocks.push({
      type: "social_links",
      directory: fieldBool(config, "social_show_directory"),
      facebook: fieldBool(config, "social_show_facebook"),
      website: fieldBool(config, "social_show_website"),
    });
  }

  if (sectionVisible(config, template.landingSections.find((s) => s.id === "email")!)) {
    blocks.push({
      type: "email_link",
      ...(str("email_headline") ? { headline: str("email_headline") } : {}),
      ...(str("email_body") ? { body: str("email_body") } : {}),
      linkLabel: str("email_link_label") || "Send email",
    });
  }

  if (sectionVisible(config, template.landingSections.find((s) => s.id === "contact")!)) {
    blocks.push({
      type: "contact_form",
      ...(str("contact_headline") ? { headline: str("contact_headline") } : {}),
      ...(str("contact_body") ? { body: str("contact_body") } : {}),
      ...(str("contact_submit_label") ? { submitLabel: str("contact_submit_label") } : {}),
    });
  }

  return blocks;
}

function renderStorySectionHtml(config: FundingPageTemplateConfig, template: FundingPageTemplateDef): string {
  const storySection = template.landingSections.find((s) => s.id === "story");
  if (!storySection || !sectionVisible(config, storySection)) return "";
  const heading = fieldString(config, "story_heading").trim();
  const bodyHtml = sanitizeFundingPageRichText(fieldString(config, "story_body"));
  if (!heading && !bodyHtml.trim()) return "";
  const headingHtml = heading ? `<h2>${escapeHtml(heading)}</h2>` : "";
  return `<section class="story">${headingHtml}<div class="story-rich">${bodyHtml}</div></section>`;
}

/** Insert story section into appeal page body (after header, before other body sections). */
function injectStorySection(html: string, storySectionHtml: string): string {
  if (!storySectionHtml.trim()) return html;
  const bodyOpen = '<div class="page-body">';
  const idx = html.indexOf(bodyOpen);
  if (idx < 0) return html;
  const insertAt = idx + bodyOpen.length;
  return `${html.slice(0, insertAt)}${storySectionHtml}${html.slice(insertAt)}`;
}

/**
 * Render landing page HTML from template config. Returns empty string when landing is disabled.
 */
export function renderFundingLandingPageHtml(
  config: FundingPageTemplateConfig,
  options: FundingPageRenderOptions,
): string {
  if (options.landingEnabled === false) return "";

  const template = getFundingPageTemplate(config.templateId);
  const preset = getFundingPageThemePreset(template, config.themePresetId);
  const blocks = fundingPageConfigToLandingBlocks(config, template);
  const storyHtml = renderStorySectionHtml(config, template);
  if (blocks.length === 0 && !storyHtml.trim()) return "";
  if (blocks.length === 0) {
    blocks.push({ type: "hero", title: fieldString(config, "hero_title") || "Support our appeal" });
  }

  const ctx: LandingPageRenderContext = {
    appealId: options.appealId,
    inboundEmailDomain: options.inboundEmailDomain,
    pageStyle: preset.pageStyle,
    template: "appeal",
    contactFormPreview: options.contactFormPreview,
    clubSocialLinks: options.clubSocialLinks,
    donationTiers: options.donationTiers,
    currency: options.currency,
  };

  let html = renderLandingPageHtmlFromBlocks(blocks, ctx);
  html = injectStorySection(html, renderStorySectionHtml(config, template));
  return html;
}

/**
 * Render finished-appeal page HTML (same template as landing). Returns empty when disabled.
 * Published to `success_submitted_html` / `published_success_html` at `…/appeals/{slug}/finished`.
 */
export function renderFundingFinishedPageHtml(
  config: FundingPageTemplateConfig,
  options: Pick<
    FundingPageRenderOptions,
    "appealId" | "inboundEmailDomain" | "finishedEnabled" | "donationTiers" | "currency" | "clubSocialLinks"
  >,
): string {
  if (options.finishedEnabled === false) return "";
  return renderFundingLandingPageHtml(config, {
    appealId: options.appealId,
    inboundEmailDomain: options.inboundEmailDomain,
    landingEnabled: true,
    donationTiers: options.donationTiers,
    currency: options.currency,
    clubSocialLinks: options.clubSocialLinks,
  });
}

/** Combined submitted HTML for directory moderation (landing + finished when enabled). */
export function renderFundingPagesSubmittedBundle(
  landingConfig: FundingPageTemplateConfig,
  finishedConfig: FundingPageTemplateConfig,
  options: FundingPageRenderOptions,
): { landingHtml: string; finishedHtml: string; combinedForScan: string } {
  const landingHtml = renderFundingLandingPageHtml(landingConfig, options);
  const finishedHtml = renderFundingFinishedPageHtml(finishedConfig, options);
  const parts = [landingHtml, finishedHtml].filter((h) => h.trim().length > 0);
  return {
    landingHtml,
    finishedHtml,
    combinedForScan: parts.join("\n\n"),
  };
}

export {
  resolveFundingLandingPageMarkers,
  sanitizeFundingPageRichText as sanitizeFundingPageRichTextForRender,
};
